<template>
  <div class='min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'>
    <MobileMenu
      :is-open="mobileMenuOpen"
      :is-logged-in="authStore.isLoggedIn"
      :is-dark="isDark"
      @close="mobileMenuOpen = false"
      @logout="handleLogout"
      @theme-toggle="handleMobileThemeToggle"
      @language-change="handleMobileLanguageChange"
    />
    <header class="bg-white/80 backdrop-blur-md shadow-sm border-b z-50">
      <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16" >
          <div class="flex items-center gap-2">
            <NuxtLink to="/" class="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {{ app.name }}
            </NuxtLink>

            <div class="h-8 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"/>

            <NuxtLink
              to="/radar"
              class="relative group px-2 py-2 rounded-lg text-base font-semibold transition-all duration-200 hover:scale-105"
              data-test="radar-link"
            >
              <span class="relative z-10 text-green-700 group-hover:from-blue-700 group-hover:to-indigo-700 transition-all">
                {{ t('common.radar') }}
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"/>
            </NuxtLink>

            <NuxtLink
              to="/automations"
              class="relative group px-2 py-2 rounded-lg text-base font-semibold transition-all duration-200 hover:scale-105"
              data-test="automation-link"
            >
              <span class="relative z-10 text-green-700 group-hover:from-blue-700 group-hover:to-indigo-700 transition-all">
                {{ t('Automation') }}
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"/>
            </NuxtLink>
          </div>

          <div class="flex items-center gap-2 sm:gap-3">
            <!-- Notification Bell (Desktop) -->
            <NotificationBell v-if="authStore.isLoggedIn" class=" md:block" />

            <button
              v-if="showTelegramIcon"
              class="flex h-9 items-center justify-center px-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 hover:scale-105 group"
              data-test="telegram-modal-open-button"
              @click="telegramModalOpen = true"
            >
              <PhTelegramLogo :size="18" class="text-gray-600 group-hover:text-sky-600 transition-colors" />
            </button>

            <!-- Theme Toggle (Desktop) -->
            <button
              class="hidden md:flex h-9 items-center gap-2 px-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 hover:scale-105 group"
              data-test="theme-toggle-button"
              :aria-label="isDark ? t('common.switchToLightMode') : t('common.switchToDarkMode')"
              @click="toggleTheme"
            >
              <PhMoonStars v-if="!isDark" :size="16" class="text-gray-600 group-hover:text-blue-600 transition-colors" />
              <PhSun v-else :size="16" class="text-gray-600 group-hover:text-blue-600 transition-colors" />
              <span class="text-xs font-medium text-gray-700 whitespace-nowrap">
                {{ isDark ? t('common.lightMode') : t('common.darkMode') }}
              </span>
            </button>

            <!-- Mobile Menu Button -->
            <button
              class="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
              data-test="mobile-menu-button"
              @click="mobileMenuOpen = !mobileMenuOpen"
            >
              <PhX v-if="mobileMenuOpen" :size="28" weight="bold" />
              <PhList v-else :size="28" />
            </button>

            <!-- Language Switcher (Desktop) -->
            <div ref="languageDropdownRef" class="hidden md:block relative" data-test="language-switcher">
              <button
                class="flex h-9 items-center gap-2 px-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 hover:scale-105 group"
                @click="languageDropdownOpen = !languageDropdownOpen"
              >
                <PhGlobe :size="20" class="text-gray-600 group-hover:text-blue-600 transition-colors" />
                <span class="text-xs font-medium text-gray-700">
                  {{ $i18n.locale.value === 'fa' ? t('common.persian') : t('common.english') }}
                </span>
                <PhCaretDown
                  :size="8"
                  class="text-gray-500 transition-transform duration-200"
                  :class="{ 'rotate-180': languageDropdownOpen }"
                />
              </button>

              <!-- Dropdown Menu -->
              <Transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <div
                  v-if="languageDropdownOpen"
                  class="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
                >
                  <div class="py-1">
                    <!-- English Option -->
                    <button
                      class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                      :class="{ 'bg-gradient-to-r from-blue-50 to-indigo-50': $i18n.locale.value === 'en' }"
                      @click="switchLanguage('en')"
                    >
                      <span class="text-sm font-medium text-gray-700 group-hover:text-blue-700 transition-colors">
                        {{ t('common.english') }}
                      </span>
                      <PhCheck
                        v-if="$i18n.locale.value === 'en'"
                        :size="18"
                        class="text-blue-600"
                        weight="bold"
                      />
                    </button>

                    <!-- Persian Option -->
                    <button
                      class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                      :class="{ 'bg-gradient-to-r from-blue-50 to-indigo-50': $i18n.locale.value === 'fa' }"
                      @click="switchLanguage('fa')"
                    >
                      <span class="text-sm font-medium text-gray-700 group-hover:text-blue-700 transition-colors">
                        {{ t('common.persian') }}
                      </span>
                      <PhCheck
                        v-if="$i18n.locale.value === 'fa'"
                        :size="18"
                        class="text-blue-600"
                        weight="bold"
                      />
                    </button>
                  </div>
                </div>
              </Transition>
            </div>

            <!-- Auth Button (Desktop) -->
            <button
              v-if="authStore.isLoggedIn"
              class="hidden md:inline-flex h-9 items-center justify-center px-4 text-center text-sm font-medium text-gray-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-white/80 dark:hover:bg-slate-700/80 border border-transparent dark:border-slate-700/70 transition-all hover:shadow-sm"
              data-test="logout-button"
              @click="handleLogout"
            >
              {{ t('auth.logout') }}
            </button>
            <NuxtLink
              v-else
              to="/login"
              class="hidden md:inline-flex h-9 items-center justify-center px-4 text-sm text-center font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md hover:scale-105"
              data-test="login-button"
            >
              {{ t('auth.login') }}
            </NuxtLink>
          </div>
        </div>
      </nav>
    </header>

    <main class="flex-1">
      <slot />
    </main>

    <TelegramJoinModal
      v-model="telegramModalOpen"
      :telegram-group-name="telegramGroupName"
      :invitation-link="telegramLink"
      @later="onTelegramLater"
      @never="onTelegramNever"
    />

    <Toaster
      :theme="isDark ? 'dark' : 'light'"
      :position="$i18n.locale.value === 'fa' ? 'top-left' : 'top-right'"
      :close-button="true"
      rich-colors
      :visible-toasts="5"
      :expand="true"
    />

    <NotificationBridge />

    <footer class="bg-white/80 backdrop-blur-md border-t">
      <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <p
          class="text-center text-gray-500 text-sm cursor-pointer select-none"
          :class="{ 'text-green-600': isDevMode }"
          @click="handleFooterClick"
        >
          {{ t('©')}} {{ new Date().getFullYear() }} {{ app.name }}
          {{ t('․') }}
          {{ t('common.allRights') }}
          <span v-if="isDevMode" class="ml-2 text-green-600 font-bold">{{ t('DEV MODE') }}</span>
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#i18n'
import { computed, ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue'
import { useDevMode } from '~/composables/useDevMode'
import { useThemeMode } from '~/composables/useThemeMode'
import { useAuthStore } from '#imports'
import { useConfig } from '~/composables/useConfig'
import { useTheme as useVuetifyTheme } from 'vuetify'
import NotificationBell from '~/components/NotificationToast/NotificationBell.vue'
import { PhList, PhGlobe, PhCaretDown, PhCheck, PhX, PhMoonStars, PhSun, PhTelegramLogo } from '@phosphor-icons/vue'
import { Toaster } from 'vue-sonner'
import NotificationBridge from '~/components/NotificationToast/NotificationBridge.vue'

// This is fully typed
const { t } = useI18n()
const { $i18n } = useNuxtApp()
const authStore = useAuthStore()
const { app } = useConfig()
const vuetifyTheme = useVuetifyTheme()

// Dev mode state
const { isDevMode, checkDevMode, toggleDevMode } = useDevMode()
const { isDark, toggleTheme, initTheme } = useThemeMode()
const clickCount = ref(0)
const clickTimeout: Ref<ReturnType<typeof setTimeout> | null> = ref(null)
const mobileMenuOpen = ref(false)
const languageDropdownOpen = ref(false)
const languageDropdownRef = ref<HTMLElement | null>(null)
const telegramModalOpen = ref(false)
const telegramLink = ref<string | null>(null)
const telegramGroupName = ref('')

type TelegramModalPreference = 'unset' | 'joined' | 'later' | 'never'

const telegramPreference = ref<TelegramModalPreference>('unset')
const telegramPreferenceKey = ref<string | null>(null)
const TELEGRAM_PREFERENCE_PREFIX = 'telegram-onboarding-preference'

interface TelegramGroupResponse {
  telegram_link: string | null
  telegram_group_name: string
  telegram_chat_id: string
}

const showTelegramIcon = computed(
  () =>
    authStore.isLoggedIn &&
    Boolean(telegramGroupName.value)
)

const readTelegramPreference = (): TelegramModalPreference => {
  if (!telegramPreferenceKey.value || import.meta.server) {
    return 'unset'
  }

  const stored = localStorage.getItem(telegramPreferenceKey.value)
  if (stored === 'joined' || stored === 'later' || stored === 'never') {
    return stored
  }

  return 'unset'
}

const writeTelegramPreference = (value: Exclude<TelegramModalPreference, 'unset'>) => {
  telegramPreference.value = value
  if (!telegramPreferenceKey.value || import.meta.server) {
    return
  }
  localStorage.setItem(telegramPreferenceKey.value, value)
}

const initTelegramOnboarding = async () => {
  const currentUser = await authStore.getUser()
  if (!currentUser) {
    return
  }

  telegramPreferenceKey.value = `${TELEGRAM_PREFERENCE_PREFIX}:${currentUser.id}`
  telegramPreference.value = readTelegramPreference()

  try {
    await $fetch('/api/telegram/group', { method: 'POST' })
    const group = await $fetch<TelegramGroupResponse>('/api/telegram/group')
    telegramLink.value = group.telegram_link
    telegramGroupName.value = group.telegram_group_name

    if (telegramPreference.value === 'unset' || telegramPreference.value === 'later') {
      telegramModalOpen.value = true
    }
  } catch (error) {
    console.error('Failed to initialize Telegram onboarding:', error)
  }
}

const onTelegramLater = () => {
  writeTelegramPreference('later')
  telegramModalOpen.value = false
}

const onTelegramNever = () => {
  writeTelegramPreference('never')
  telegramModalOpen.value = false
}

const syncVuetifyTheme = (darkMode: boolean): void => {
  vuetifyTheme.global.name.value = darkMode ? 'bittripDark' : 'bittripLight'
}

onMounted(async () => {
  initTheme()
  syncVuetifyTheme(isDark.value)
  checkDevMode()

  await authStore.fetchUser()
})

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  if (languageDropdownRef.value && !languageDropdownRef.value.contains(target)) {
    languageDropdownOpen.value = false
  }
}

// Watch for dropdown open state
watch(languageDropdownOpen, (isOpen) => {
  if (isOpen) {
    // Use nextTick to avoid immediate triggering
    nextTick(() => {
      document.addEventListener('click', handleClickOutside)
    })
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(isDark, (darkMode) => {
  syncVuetifyTheme(darkMode)
}, { immediate: true })

watch(
  () => authStore.isLoggedIn,
  async (loggedIn) => {
    if (!loggedIn) {
      telegramModalOpen.value = false
      telegramLink.value = null
      telegramGroupName.value = ''
      telegramPreference.value = 'unset'
      telegramPreferenceKey.value = null
      return
    }

    if (!telegramGroupName.value) {
      await initTelegramOnboarding()
    }
  }
)

const switchLanguage = async (locale: 'fa' | 'en') => {
  if ($i18n.locale.value === locale) {
    languageDropdownOpen.value = false
    return
  }

  try {
    await $i18n.setLocale(locale)
    languageDropdownOpen.value = false

    if (authStore.isLoggedIn) {
      await authStore.updatePreferences({ default_locale: locale })
    }
  } catch (error) {
    console.error('Error switching language:', error)
  }
}

const handleMobileLanguageChange = async (locale: 'fa' | 'en') => {
  mobileMenuOpen.value = false
  try {
    await $i18n.setLocale(locale)

    if (authStore.isLoggedIn) {
      await authStore.updatePreferences({ default_locale: locale })
    }
  } catch (error) {
    console.error('Error switching language:', error)
  }
}


const handleLogout = () => {
  telegramModalOpen.value = false
  telegramLink.value = null
  telegramGroupName.value = ''
  telegramPreference.value = 'unset'
  telegramPreferenceKey.value = null
  authStore.logout()
  mobileMenuOpen.value = false
}

const handleMobileThemeToggle = () => {
  toggleTheme()
  mobileMenuOpen.value = false
}

const handleFooterClick = () => {
  clickCount.value++

  // Clear existing timeout
  if (clickTimeout.value) {
    clearTimeout(clickTimeout.value)
  }

  // Set timeout to reset click count after 2 seconds
  clickTimeout.value = setTimeout(() => {
    clickCount.value = 0
  }, 2000)

  // Check if we reached 5 clicks
  if (clickCount.value >= 5) {
    // Toggle dev mode
    toggleDevMode()

    // Reset click count
    clickCount.value = 0
    clearTimeout(clickTimeout.value)
  }
}
</script>
